import { Shield, Mail, Clock, Database, Users, Lock, Eye, FileText, ArrowLeft, Home } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { useNavigate } from 'react-router-dom';

const PrivacyPolicy = () => {
  const navigate = useNavigate();
  const lastUpdated = "January 21, 2025";

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto py-12 px-4 max-w-4xl">
        {/* Navigation Header */}
        <div className="flex gap-2 mb-8">
          <Button variant="outline" size="sm" onClick={() => navigate('/account-settings')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Account Settings
          </Button>
          <Button variant="outline" size="sm" onClick={() => navigate('/')}>
            <Home className="h-4 w-4 mr-2" />
            Home
          </Button>
        </div>
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-foreground mb-4">Privacy Policy</h1>
          <p className="text-lg text-muted-foreground">
            How we protect and handle your personal health data
          </p>
          <p className="text-sm text-muted-foreground mt-2">
            Last updated: {lastUpdated}
          </p>
        </div>

        <div className="space-y-8">
          {/* Introduction */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5" />
                Our Commitment to Your Privacy
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
            <p className="text-muted-foreground">
                At Am I OK? - Health Advisor ("we," "our," or "us"), developed by Health Assessment Solutions, 
                we take your privacy seriously. This Privacy Policy explains how we collect, use, disclose, 
                and safeguard your information when you use our mobile application and web service for health assessments.
              </p>
              <p className="text-muted-foreground">
                <strong>Important:</strong> Am I OK? - Health Advisor provides health assessment tools for 
                informational purposes only and is not intended to replace professional medical advice, diagnosis, or treatment.
              </p>
            </CardContent>
          </Card>

          {/* Information We Collect */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Database className="h-5 w-5" />
                Information We Collect
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <h3 className="font-semibold mb-2">Personal Information</h3>
                <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
                  <li>Email address (for account creation and communication)</li>
                  <li>Full name (for personalization and account management)</li>
                  <li>Account preferences and settings</li>
                </ul>
              </div>
              
              <div>
                <h3 className="font-semibold mb-2">Health Assessment Data</h3>
                <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
                  <li>Responses to health assessment questionnaires</li>
                  <li>Symptom descriptions and health concerns</li>
                  <li>Assessment results and risk scores</li>
                  <li>Assessment timestamps and session data</li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold mb-2">Technical Information</h3>
                <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
                  <li>Device information (type, operating system, app version)</li>
                  <li>Usage analytics (features used, session duration)</li>
                  <li>Error logs and performance data</li>
                  <li>IP address and general location information</li>
                </ul>
              </div>
            </CardContent>
          </Card>

          {/* How We Use Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Eye className="h-5 w-5" />
                How We Use Your Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h3 className="font-semibold mb-2">Primary Uses</h3>
                <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
                  <li>Provide personalized health assessment results</li>
                  <li>Generate evidence-based health insights and recommendations</li>
                  <li>Maintain your assessment history for trend analysis</li>
                  <li>Improve the accuracy and relevance of our health algorithms</li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold mb-2">Service Operations</h3>
                <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
                  <li>Authenticate your account and maintain security</li>
                  <li>Send important updates about our service</li>
                  <li>Provide customer support and respond to inquiries</li>
                  <li>Analyze usage patterns to improve our application</li>
                </ul>
              </div>
            </CardContent>
          </Card>

          {/* Data Storage and Security */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Lock className="h-5 w-5" />
                Data Storage and Security
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h3 className="font-semibold mb-2">Data Storage</h3>
                <p className="text-sm text-muted-foreground">
                  Your data is stored securely using Supabase, a trusted cloud database provider. 
                  All data is encrypted in transit and at rest using industry-standard encryption methods.
                </p>
              </div>

              <div>
                <h3 className="font-semibold mb-2">Security Measures</h3>
                <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
                  <li>Row-Level Security (RLS) ensures users can only access their own data</li>
                  <li>Secure authentication using encrypted tokens</li>
                  <li>Regular security audits and updates</li>
                  <li>Limited access to data by authorized personnel only</li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold mb-2">Data Retention</h3>
                <p className="text-sm text-muted-foreground">
                  We retain your personal data for as long as your account is active or as needed to 
                  provide our services. You may request deletion of your account and all associated 
                  data at any time.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Data Sharing */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                Information Sharing and Disclosure
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h3 className="font-semibold mb-2">We Do NOT Share Your Personal Health Data</h3>
                <p className="text-sm text-muted-foreground">
                  We do not sell, trade, or otherwise transfer your personal health information to 
                  third parties for marketing or commercial purposes.
                </p>
              </div>

              <div>
                <h3 className="font-semibold mb-2">Limited Disclosure</h3>
                <p className="text-sm text-muted-foreground">We may disclose information only in these circumstances:</p>
                <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
                  <li>With your explicit consent</li>
                  <li>To comply with legal obligations or court orders</li>
                  <li>To protect our rights, property, or safety</li>
                  <li>In case of emergency where disclosure may prevent harm</li>
                </ul>
              </div>

              <div>
                <h3 className="font-semibold mb-2">Service Providers</h3>
                <p className="text-sm text-muted-foreground">
                  We use trusted service providers (like Supabase for data storage) who are bound by 
                  strict confidentiality agreements and data protection standards.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Your Rights */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Your Privacy Rights
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">You have the following rights regarding your personal data:</p>
              
              <div className="grid gap-4">
                <div>
                  <h4 className="font-medium">Access and Portability</h4>
                  <p className="text-sm text-muted-foreground">
                    Request a copy of all personal data we hold about you in a portable format.
                  </p>
                </div>

                <div>
                  <h4 className="font-medium">Correction</h4>
                  <p className="text-sm text-muted-foreground">
                    Update or correct your personal information through your account settings.
                  </p>
                </div>

                <div>
                  <h4 className="font-medium">Deletion</h4>
                  <p className="text-sm text-muted-foreground">
                    Request permanent deletion of your account and all associated data.
                  </p>
                </div>

                <div>
                  <h4 className="font-medium">Withdrawal of Consent</h4>
                  <p className="text-sm text-muted-foreground">
                    Withdraw consent for data processing at any time (may limit service functionality).
                  </p>
                </div>
              </div>

              <p className="text-sm text-muted-foreground">
                <strong>To exercise these rights:</strong> Contact us at jm@magnavisio.co.uk or use the 
                account deletion request page in our app.
              </p>
            </CardContent>
          </Card>

          {/* International Users */}
          <Card>
            <CardHeader>
              <CardTitle>International Users</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                Our services are hosted in the United States. If you are accessing our services from 
                outside the United States, please be aware that your information may be transferred to, 
                stored, and processed in the United States where our servers are located and our central 
                database is operated.
              </p>
            </CardContent>
          </Card>

          {/* Children's Privacy */}
          <Card>
            <CardHeader>
              <CardTitle>Children's Privacy</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                Our service is not intended for children under 13 years of age. We do not knowingly 
                collect personal information from children under 13. If you are a parent or guardian 
                and believe your child has provided us with personal information, please contact us 
                immediately.
              </p>
            </CardContent>
          </Card>

          {/* Updates to Policy */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5" />
                Updates to This Privacy Policy
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                We may update this Privacy Policy from time to time. We will notify you of any changes 
                by posting the new Privacy Policy on this page and updating the "Last updated" date. 
                We encourage you to review this Privacy Policy periodically for any changes.
              </p>
            </CardContent>
          </Card>

          {/* Contact Information */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="h-5 w-5" />
                Contact Us
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                If you have any questions about this Privacy Policy or our data practices, please contact us:
              </p>
              
              <div className="space-y-2 text-sm">
                <p><strong>Email:</strong> jm@magnavisio.co.uk</p>
                <p><strong>Privacy Officer:</strong> jm@magnavisio.co.uk</p>
                <p><strong>Response Time:</strong> Within 30 days for privacy-related inquiries</p>
                <p><strong>App Store Listing:</strong> Am I OK? - Health Advisor</p>
                <p><strong>Developer:</strong> Health Assessment Solutions</p>
              </div>

              <div className="mt-4 p-4 bg-muted rounded-lg">
                <p className="text-sm font-medium">Data Protection Compliance</p>
                <p className="text-xs text-muted-foreground">
                  This privacy policy complies with GDPR, CCPA, and Google Play Developer Policy requirements 
                  for Am I OK? - Health Advisor by Health Assessment Solutions.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        <Separator className="my-8" />

        {/* Footer */}
        <div className="text-center text-sm text-muted-foreground">
          <p>© 2025 Health Assessment Solutions. All rights reserved.</p>
          <p className="mt-2">
            This privacy policy is effective as of {lastUpdated} and applies to all users of the Am I OK? - Health Advisor application.
          </p>
          <p className="mt-1 text-xs">
            App Store Listing: Am I OK? - Health Advisor | Developer: Health Assessment Solutions
          </p>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;